import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import LeadTableHeader from './components/LeadTableHeader';
import LeadTableRow from './components/LeadTableRow';
import PipelineVisualization from './components/PipelineVisualization';
import LeadStatsCards from './components/LeadStatsCards';
import AISalesFunnelPanel from './components/AISalesFunnelPanel';
import AddLeadModal from './components/AddLeadModal';
import SendCampaignModal from './components/SendCampaignModal';
import LeadDetailsModal from './components/LeadDetailsModal';
import BulkUploadModal from './components/BulkUploadModal';
import Icon from '../../components/AppIcon';
import { Brain, Upload, Download, AlertCircle } from 'lucide-react';
import PageLayout from '../../components/ui/PageLayout';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/Toast';

const LeadManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isInitialized, loading: authLoading } = useAuth();
  const toast = useToast();
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState('table');
  const [selectedLead, setSelectedLead] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isAddLeadModalOpen, setIsAddLeadModalOpen] = useState(false);
  const [isSendCampaignModalOpen, setIsSendCampaignModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [showAIPanel, setShowAIPanel] = useState(false);

  useEffect(() => {
    if (!isInitialized || authLoading) return;
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    loadLeads();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isInitialized, authLoading]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location?.search);
    const mode = searchParams?.get('mode');
    if (mode === 'bulk') {
      setIsBulkMode(true);
      setViewMode('table');
    }
  }, [location?.search]);

  const loadLeads = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      setError(null);

      const { data, error: leadsError } = await supabase
        ?.from('funnel_leads')
        ?.select('id, first_name, last_name, email, phone, company, job_title, lead_score, is_converted, source, total_value, created_at, updated_at')
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(100);

      if (leadsError) {
        console.warn('Error loading leads:', leadsError?.message);
        setLeads([]);
        setFilteredLeads([]);
        return;
      }

      const transformedLeads = (data || [])?.map(lead => ({
        id: lead?.id,
        name: `${lead?.first_name || ''} ${lead?.last_name || ''}`?.trim() || lead?.email || 'Unknown',
        email: lead?.email || '',
        phone: lead?.phone || '',
        company: lead?.company || '',
        title: lead?.job_title || '',
        status: lead?.is_converted ? 'converted' : (lead?.lead_score >= 80 ? 'qualified' : lead?.lead_score >= 50 ? 'contacted' : 'new'),
        score: lead?.lead_score || 0,
        estimatedValue: lead?.total_value || 0,
        avatar: null,
        assignee: null,
        lastInteraction: lead?.updated_at,
        createdAt: lead?.created_at,
        interactions: 0,
        source: lead?.source || 'Unknown'
      }));

      setLeads(transformedLeads);
      setFilteredLeads(transformedLeads);
    } catch (err) {
      console.error('Error loading leads:', err);
      setError(err?.message || 'Failed to load leads');
      setLeads([]);
      setFilteredLeads([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    let filtered = [...leads];

    if (searchQuery) {
      filtered = filtered?.filter(lead =>
        lead?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        lead?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        lead?.company?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    if (selectedStatus !== 'all') {
      filtered = filtered?.filter(lead => lead?.status === selectedStatus);
    }

    setFilteredLeads(filtered);
  }, [leads, searchQuery, selectedStatus]);

  const handleSelectLead = (leadId) => {
    setSelectedLeads(prev =>
      prev?.includes(leadId)
        ? prev?.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLeads?.length === filteredLeads?.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(filteredLeads?.map(lead => lead?.id));
    }
  };

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedLead(null);
  };

  const handleAddLead = async (leadData) => {
    if (!user?.id || !supabase) return;
    try {
      const { error: insertError } = await supabase
        ?.from('funnel_leads')
        ?.insert([{
          user_id: user?.id,
          first_name: leadData?.firstName || '',
          last_name: leadData?.lastName || '',
          email: leadData?.email || '',
          phone: leadData?.phone || '',
          company: leadData?.company || '',
          job_title: leadData?.jobTitle || '',
          lead_score: leadData?.score || 50,
          source: leadData?.source || 'manual',
          created_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        }]);
      if (insertError) throw insertError;
      toast?.success('Lead added successfully!', 'Lead Added ✓');
      setIsAddLeadModalOpen(false);
      loadLeads();
    } catch (err) {
      console.error('Error adding lead:', err);
      toast?.error(err?.message || 'Failed to add lead.', 'Error');
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!leadId || !supabase) return;
    if (!window.confirm('Delete this lead? This action cannot be undone.')) return;
    try {
      const { error } = await supabase?.from('funnel_leads')?.delete()?.eq('id', leadId);
      if (error) throw error;
      toast?.success('Lead deleted successfully.', 'Deleted');
      loadLeads();
    } catch (err) {
      toast?.error(err?.message || 'Failed to delete lead.', 'Error');
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedLeads?.length === 0) {
      toast?.warning('Please select at least one lead first.', 'No Selection');
      return;
    }

    try {
      switch (action) {
        case 'send-campaign':
          setIsSendCampaignModalOpen(true);
          break;
        case 'delete':
          if (!window.confirm(`Delete ${selectedLeads?.length} selected lead(s)? This cannot be undone.`)) return;
          if (!supabase) throw new Error('Service unavailable');
          const { error: deleteError } = await supabase
            ?.from('funnel_leads')
            ?.delete()
            ?.in('id', selectedLeads);
          if (deleteError) throw deleteError;
          toast?.success(`${selectedLeads?.length} lead(s) deleted successfully.`, 'Deleted ✓');
          setSelectedLeads([]);
          loadLeads();
          break;
        case 'export':
          handleExportLeads();
          break;
        default:
          toast?.info(`Action "${action}" applied to ${selectedLeads?.length} lead(s).`, 'Action Applied');
      }
    } catch (err) {
      console.error('Bulk action error:', err);
      toast?.error(err?.message || 'Bulk action failed.', 'Error');
    }
  };

  const handleExportLeads = () => {
    const leadsToExport = selectedLeads?.length > 0
      ? filteredLeads?.filter(l => selectedLeads?.includes(l?.id))
      : filteredLeads;

    if (leadsToExport?.length === 0) {
      toast?.warning('No leads to export.', 'Nothing to Export');
      return;
    }

    const csv = [
      'Name,Email,Phone,Company,Title,Status,Score,Source,Created',
      ...leadsToExport?.map(l =>
        `"${l?.name}",${l?.email},${l?.phone},"${l?.company}","${l?.title}",${l?.status},${l?.score},${l?.source},${l?.createdAt ? new Date(l?.createdAt)?.toLocaleDateString() : ''}`
      )
    ]?.join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads_export_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
    URL.revokeObjectURL(url);
    toast?.success(`Exported ${leadsToExport?.length} lead(s) to CSV.`, 'Export Complete ✓');
  };

  const handleBulkUpload = async (uploadedLeads) => {
    if (!user?.id || !supabase || !uploadedLeads?.length) return;
    try {
      const leadsToInsert = uploadedLeads?.map(lead => ({
        user_id: user?.id,
        first_name: lead?.firstName || lead?.first_name || '',
        last_name: lead?.lastName || lead?.last_name || '',
        email: lead?.email || '',
        phone: lead?.phone || '',
        company: lead?.company || '',
        job_title: lead?.jobTitle || lead?.job_title || '',
        lead_score: lead?.score || lead?.lead_score || 50,
        source: lead?.source || 'csv_import',
        created_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      }));

      const { error } = await supabase?.from('funnel_leads')?.insert(leadsToInsert);
      if (error) throw error;
      toast?.success(`${uploadedLeads?.length} leads imported successfully!`, 'Import Complete ✓');
      setIsBulkUploadModalOpen(false);
      loadLeads();
    } catch (err) {
      console.error('Bulk upload error:', err);
      toast?.error(err?.message || 'Failed to import leads.', 'Import Failed');
    }
  };

  const stats = useMemo(() => ({
    total: leads?.length,
    new: leads?.filter(l => l?.status === 'new')?.length,
    contacted: leads?.filter(l => l?.status === 'contacted')?.length,
    qualified: leads?.filter(l => l?.status === 'qualified')?.length,
    converted: leads?.filter(l => l?.status === 'converted')?.length,
  }), [leads]);

  return (
    <PageLayout>
      <div>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Lead Management</h1>
            <p className="text-muted-foreground text-sm mt-1">{leads?.length} total leads in your pipeline</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setShowAIPanel(p => !p)}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                showAIPanel ? 'bg-purple-600 text-white' : 'border border-border text-foreground hover:bg-muted'
              }`}
            >
              <Brain className="w-4 h-4" />
              AI Panel
            </button>
            <button
              onClick={() => setIsBulkUploadModalOpen(true)}
              className="flex items-center gap-2 px-3 py-2 border border-border rounded-xl text-sm font-medium text-foreground hover:bg-muted transition-colors"
            >
              <Upload className="w-4 h-4" />
              Bulk Upload
            </button>
            <button
              onClick={handleExportLeads}
              className="flex items-center gap-2 px-3 py-2 border border-border rounded-xl text-sm font-medium text-foreground hover:bg-muted transition-colors"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={() => setIsAddLeadModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl text-sm font-semibold transition-colors"
            >
              <Icon name="Plus" size={16} />
              Add Lead
            </button>
          </div>
        </div>

        {/* Stats */}
        <LeadStatsCards stats={stats} leads={leads} />

        {/* AI Panel */}
        {showAIPanel && (
          <div className="mb-6">
            <AISalesFunnelPanel 
              leads={filteredLeads} 
              onLeadUpdate={loadLeads}
              onActionTaken={(action) => toast?.info(`AI action: ${action}`, 'AI Assistant')}
            />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0" />
            <p className="text-sm text-destructive">{error}</p>
            <button onClick={loadLeads} className="ml-auto text-sm text-primary hover:underline">Retry</button>
          </div>
        )}

        {/* View Toggle */}
        <div className="flex items-center gap-2 mb-4">
          {['table', 'pipeline']?.map(mode => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors capitalize ${
                viewMode === mode ? 'bg-primary text-primary-foreground' : 'border border-border text-foreground hover:bg-muted'
              }`}
            >
              {mode}
            </button>
          ))}
        </div>

        {viewMode === 'pipeline' ? (
          <PipelineVisualization 
            leads={filteredLeads} 
            onStageChange={(leadId, newStage) => {
              toast?.info(`Lead stage updated to ${newStage}`, 'Stage Changed');
              loadLeads();
            }}
          />
        ) : (
          <div className="bg-card border border-border rounded-2xl shadow-elevation-1 overflow-hidden">
            <LeadTableHeader
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              selectedStatus={selectedStatus}
              onStatusChange={setSelectedStatus}
              sortBy={sortBy}
              onSortChange={setSortBy}
              selectedLeads={selectedLeads}
              onSelectAll={handleSelectAll}
              totalLeads={filteredLeads?.length}
              onBulkAction={handleBulkAction}
              isBulkMode={isBulkMode}
              onToggleBulkMode={() => setIsBulkMode(p => !p)}
              selectedAssignee={null}
              onAssigneeChange={() => {}}
              selectedCount={selectedLeads?.length}
              onAddLead={() => setIsAddLeadModalOpen(true)}
              onSendCampaign={() => setIsSendCampaignModalOpen(true)}
            />

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="text-center">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">Loading leads...</p>
                </div>
              </div>
            ) : filteredLeads?.length === 0 ? (
              <div className="text-center py-16">
                <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No Leads Found</h3>
                <p className="text-muted-foreground text-sm mb-6">
                  {searchQuery || selectedStatus !== 'all' ? 'No leads match your filters.' : 'Add your first lead to get started.'}
                </p>
                <button
                  onClick={() => setIsAddLeadModalOpen(true)}
                  className="px-5 py-2.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-semibold text-sm transition-colors"
                >
                  Add First Lead
                </button>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filteredLeads?.map(lead => (
                  <LeadTableRow
                    key={lead?.id}
                    lead={lead}
                    isSelected={selectedLeads?.includes(lead?.id)}
                    onSelect={() => handleSelectLead(lead?.id)}
                    onClick={() => handleLeadClick(lead)}
                    onDelete={() => handleDeleteLead(lead?.id)}
                    isBulkMode={isBulkMode}
                    onStatusChange={(newStatus) => {
                      toast?.info(`Lead status updated to ${newStatus}`, 'Status Changed');
                      loadLeads();
                    }}
                    onViewDetails={() => handleLeadClick(lead)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Modals */}
        {isDetailModalOpen && selectedLead && (
          <LeadDetailsModal
            lead={selectedLead}
            isOpen={isDetailModalOpen}
            onClose={handleCloseDetailModal}
            onUpdate={loadLeads}
            onSave={(updatedLead) => {
              toast?.success('Lead updated successfully', 'Updated');
              loadLeads();
            }}
          />
        )}
        {isAddLeadModalOpen && (
          <AddLeadModal
            isOpen={isAddLeadModalOpen}
            onClose={() => setIsAddLeadModalOpen(false)}
            onAdd={handleAddLead}
            onLeadAdded={loadLeads}
          />
        )}
        {isSendCampaignModalOpen && (
          <SendCampaignModal
            isOpen={isSendCampaignModalOpen}
            onClose={() => setIsSendCampaignModalOpen(false)}
            selectedLeads={filteredLeads?.filter(l => selectedLeads?.includes(l?.id))}
          />
        )}
        {isBulkUploadModalOpen && (
          <BulkUploadModal
            isOpen={isBulkUploadModalOpen}
            onClose={() => setIsBulkUploadModalOpen(false)}
            onUpload={handleBulkUpload}
            onImportComplete={loadLeads}
          />
        )}
      </div>
    </PageLayout>
  );
};

export default LeadManagement;